import React, { useState, useEffect } from 'react';
import {
    assignCourseToClientAsync,
    removeCourseFromClientAsync,
    getAssignedCoursesForClientAsync,
    populateCourseDropdownAsync,
} from '../../../Services/CourseService';
import { showToast } from '../../../Infrastructure/Notifications/ToastrConfig';
import Modal from '../../../Shared/Components/Modal/Modal';

// Client-level course (module) assignment. Lets platform admins assign a course to an entire
// client and remove it again. Mirrors AssignedPathways but for ClientCourse records.
const ClientCourseAssignments = ({ clientId, maximumNumberOfLearners }) => {
    const [allCourses, setAllCourses] = useState([]);
    const [assignedCourses, setAssignedCourses] = useState([]);
    const [selectedCourseId, setSelectedCourseId] = useState('');
    const [numberOfCourses, setNumberOfCourses] = useState(maximumNumberOfLearners || 0);
    const [courseToRemove, setCourseToRemove] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (clientId) {
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientId]);

    useEffect(() => {
        setNumberOfCourses(maximumNumberOfLearners || 0);
    }, [maximumNumberOfLearners]);

    const fetchData = async () => {
        try {
            setIsLoading(true);
            const [dropdown, assigned] = await Promise.all([
                populateCourseDropdownAsync(),
                getAssignedCoursesForClientAsync(clientId),
            ]);
            setAllCourses(dropdown.courses || []);
            setAssignedCourses(assigned.courses || []);
        } catch (error) {
            console.error('Error loading client courses:', error);
            showToast(false, 'Failed to load courses');
        } finally {
            setIsLoading(false);
        }
    };

    const handleAssignCourse = async () => {
        if (!selectedCourseId) {
            showToast(false, 'Please select a course');
            return;
        }

        try {
            const response = await assignCourseToClientAsync({
                clientId: clientId,
                courseId: selectedCourseId,
                numberOfCourses: Number(numberOfCourses) || 0,
            });

            if (response) {
                showToast(true, 'Course assigned to client');
                setSelectedCourseId('');
                fetchData();
            } else {
                showToast(false, 'Failed to assign course');
            }
        } catch (error) {
            console.error('Error assigning course:', error);
            showToast(false, 'Failed to assign course');
        }
    };

    const handleRemoveCourse = async () => {
        try {
            const response = await removeCourseFromClientAsync({
                clientId: clientId,
                courseId: courseToRemove.id,
            });

            if (response && response.success !== false) {
                showToast(true, 'Course removed from client');
                fetchData();
            } else {
                showToast(false, response?.errors?.[0]?.error || 'Failed to remove course');
            }
        } catch (error) {
            console.error('Error removing course:', error);
            showToast(false, 'Failed to remove course');
        }
    };

    return (
        <>
            <Modal
                modalId="removeClientCourseModal"
                bodyText={`Are you sure you want to remove ${courseToRemove.title} from this client? Learners will lose access unless the course is assigned via a group, pathway, or directly.`}
                buttonFunc={handleRemoveCourse}
                buttonText="Remove"
                title="Remove Course"
            />

            <div>
                {/* Assign Course Section */}
                <div style={{
                    background: 'linear-gradient(135deg, #f6f8fb 0%, #ffffff 100%)',
                    borderRadius: '12px',
                    padding: '20px',
                    border: '2px solid #e5e7eb',
                    marginBottom: '24px'
                }}>
                    <label style={{
                        display: 'block',
                        fontSize: '0.9rem',
                        fontWeight: '600',
                        color: '#64748b',
                        marginBottom: '12px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                    }}>
                        Assign Course
                    </label>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <select
                            value={selectedCourseId}
                            onChange={(e) => setSelectedCourseId(e.target.value)}
                            className="form-control"
                            style={{
                                flex: '1',
                                minWidth: '220px',
                                padding: '12px 16px',
                                fontSize: '1rem',
                                border: '2px solid #e5e7eb',
                                borderRadius: '8px',
                                height: '48px'
                            }}
                        >
                            <option value="">Select course...</option>
                            {allCourses.map((course) => (
                                <option key={course.id} value={course.id}>
                                    {course.title} ({course.hasAssessment ? 'Assessment' : 'Mindfullness'})
                                </option>
                            ))}
                        </select>
                        <input
                            type="number"
                            min="0"
                            value={numberOfCourses}
                            onChange={(e) => setNumberOfCourses(e.target.value)}
                            title="Number of licenses / seats"
                            style={{
                                width: '120px',
                                padding: '12px 16px',
                                fontSize: '1rem',
                                border: '2px solid #e5e7eb',
                                borderRadius: '8px',
                                height: '48px'
                            }}
                        />
                        <button
                            onClick={handleAssignCourse}
                            style={{
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '12px 24px',
                                fontSize: '1rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                                height: '48px',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            Assign
                        </button>
                    </div>
                </div>

                {/* Assigned Courses Table */}
                <div style={{
                    background: 'white',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    border: '1px solid #e5e7eb'
                }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead style={{ background: '#f8fafc' }}>
                            <tr>
                                <th style={{ padding: '16px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', fontWeight: '600', color: '#1e293b' }}>
                                    Course Title
                                </th>
                                <th style={{ padding: '16px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', fontWeight: '600', color: '#1e293b', width: '120px' }}>
                                    Licenses
                                </th>
                                <th style={{ padding: '16px', textAlign: 'right', borderBottom: '2px solid #e5e7eb', width: '100px' }}>
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <tr>
                                    <td colSpan="3" style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                                        Loading courses...
                                    </td>
                                </tr>
                            ) : assignedCourses.length === 0 ? (
                                <tr>
                                    <td colSpan="3" style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                                        No courses assigned to this client yet
                                    </td>
                                </tr>
                            ) : (
                                assignedCourses.map((course) => (
                                    <tr key={course.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        <td style={{ padding: '16px', color: '#1e293b' }}>
                                            {course.title}
                                        </td>
                                        <td style={{ padding: '16px', textAlign: 'center', color: '#64748b' }}>
                                            {course.numberAvailable}
                                        </td>
                                        <td style={{ padding: '16px', textAlign: 'right' }}>
                                            <button
                                                data-target="#removeClientCourseModal"
                                                data-toggle="modal"
                                                onClick={() => setCourseToRemove(course)}
                                                style={{
                                                    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    padding: '6px 14px',
                                                    fontSize: '0.85rem',
                                                    fontWeight: '600',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                Remove
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
};

export default ClientCourseAssignments;
